import React, { useState, useEffect } from 'react'
import { 
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Package,
  DollarSign,
  Calendar,
  MapPin,
  Clock,
  Target,
  Percent,
  Star,
  Filter,
  Download
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { formatPrice } from '../../lib/square'
import LoadingSpinner from '../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface AnalyticsData {
  // Customer Metrics
  totalCustomers: number
  newCustomersThisMonth: number
  returningCustomers: number
  customerGrowthRate: number

  // Order Metrics
  totalOrders: number
  averageOrderValue: number
  orderCompletionRate: number
  orderTrends: Array<{ date: string; orders: number; revenue: number }>

  // Event Metrics
  totalEvents: number
  averageEventCapacity: number
  eventUtilizationRate: number
  popularLocations: Array<{ location: string; events: number; orders: number }>

  // Revenue Metrics
  totalRevenue: number
  revenueThisMonth: number
  revenueGrowthRate: number
  revenueByEvent: Array<{ eventTitle: string; revenue: number; orders: number }>

  // Engagement Metrics
  averageTimeToOrder: number
  popularItems: Array<{ itemName: string; orders: number; revenue: number }>
  orderStatusDistribution: Record<string, number>
  customerRetentionRate: number

  // Phase 6: Lobster Tail Capacity Metrics
  capacityUtilization: {
    averageUtilization: number
    soldOutEvents: number
    eventsByTier: Array<{ tier: string; count: number }>
    totalTailsOrdered: number
    totalTailCapacity: number
  }
  packagePopularity: Array<{
    tailCount: number
    itemCount: number
    totalOrders: number
    revenue: number
  }>
}

const Analytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')

  useEffect(() => {
    loadAnalytics()
  }, [timeRange])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      
      // Calculate date range
      const now = new Date()
      const startDate = new Date()
      switch (timeRange) {
        case '7d':
          startDate.setDate(now.getDate() - 7)
          break
        case '30d':
          startDate.setDate(now.getDate() - 30)
          break
        case '90d':
          startDate.setDate(now.getDate() - 90)
          break
        case 'all':
          startDate.setFullYear(2020) // Far enough back
          break
      }

      // Fetch data in parallel
      const [
        usersResult,
        ordersResult,
        eventsResult,
        orderItemsResult,
        menuItemsResult
      ] = await Promise.allSettled([
        // Users data
        supabase.from('secret_users').select('id, created_at'),

        // Orders data
        supabase.from('secret_orders').select(`
          id,
          total_amount,
          status,
          created_at,
          user_id,
          secret_events (
            id,
            title,
            location_name
          )
        `).gte('created_at', startDate.toISOString()),

        // Events data - Phase 6: Include lobster tail capacity fields
        supabase.from('secret_events').select(`
          id,
          title,
          location_name,
          max_capacity,
          current_orders,
          max_lobster_tails,
          created_at
        `),

        // Order items data
        supabase.from('secret_order_items').select(`
          quantity,
          total_price,
          secret_menu_items (
            name
          ),
          secret_orders!inner (
            created_at
          )
        `).gte('secret_orders.created_at', startDate.toISOString()),

        // Phase 6: Menu items with lobster tail quantities
        supabase.from('secret_menu_items').select('id, name, lobster_tail_qty')
      ])

      const users = usersResult.status === 'fulfilled' ? usersResult.value.data || [] : []
      const orders = ordersResult.status === 'fulfilled' ? ordersResult.value.data || [] : []
      const events = eventsResult.status === 'fulfilled' ? eventsResult.value.data || [] : []
      const orderItems = orderItemsResult.status === 'fulfilled' ? orderItemsResult.value.data || [] : []
      const menuItems = menuItemsResult.status === 'fulfilled' ? menuItemsResult.value.data || [] : []

      // Calculate analytics - Phase 6: Pass menu items
      const analytics = calculateAnalytics(users, orders, events, orderItems, menuItems, startDate)
      setData(analytics)
      
    } catch (error) {
      console.error('Failed to load analytics:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type='error'
          title='Loading Failed'
          message='Failed to load analytics data. Please try again.'
        />
      ))
    } finally {
      setLoading(false)
    }
  }

  const calculateAnalytics = (
    users: any[],
    orders: any[],
    events: any[],
    orderItems: any[],
    menuItems: any[],
    startDate: Date
  ): AnalyticsData => {
    const now = new Date()
    const monthAgo = new Date()
    monthAgo.setMonth(now.getMonth() - 1)

    // Customer metrics
    const totalCustomers = users.length
    const newCustomersThisMonth = users.filter(u => 
      new Date(u.created_at) >= monthAgo
    ).length
    const uniqueOrderUsers = new Set(orders.map(o => o.user_id))
    const returningCustomers = users.filter(u => {
      const userOrders = orders.filter(o => o.user_id === u.id)
      return userOrders.length > 1
    }).length

    // Order metrics
    const completedOrders = orders.filter(o => o.status === 'completed')
    // Task 6: Only count confirmed/completed orders for revenue (exclude pending)
    const paidOrders = orders.filter(o =>
      o.status === 'confirmed' || o.status === 'completed'
    )
    const totalRevenue = paidOrders
      .reduce((sum, o) => sum + (o.total_amount || 0), 0)
    const averageOrderValue = paidOrders.length > 0
      ? totalRevenue / paidOrders.length
      : 0
    const orderCompletionRate = orders.length > 0 
      ? (completedOrders.length / orders.length) * 100 
      : 0

    // Revenue trends by week
    const orderTrends = generateOrderTrends(orders, startDate)

    // Event metrics
    const totalEvents = events.length
    const averageEventCapacity = events.length > 0
      ? events.reduce((sum, e) => sum + (e.max_capacity || 0), 0) / events.length
      : 0
    const totalCapacity = events.reduce((sum, e) => sum + (e.max_capacity || 0), 0)
    const totalUtilization = events.reduce((sum, e) => sum + (e.current_orders || 0), 0)
    const eventUtilizationRate = totalCapacity > 0 
      ? (totalUtilization / totalCapacity) * 100 
      : 0

    // Popular locations
    const locationMap = new Map()
    orders.forEach(order => {
      if (order.secret_events?.location_name) {
        const location = order.secret_events.location_name
        if (!locationMap.has(location)) {
          locationMap.set(location, { events: 0, orders: 0 })
        }
        locationMap.get(location).orders++
      }
    })
    events.forEach(event => {
      if (event.location_name) {
        if (!locationMap.has(event.location_name)) {
          locationMap.set(event.location_name, { events: 0, orders: 0 })
        }
        locationMap.get(event.location_name).events++
      }
    })
    const popularLocations = Array.from(locationMap.entries())
      .map(([location, stats]) => ({ location, ...stats }))
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 5)

    // Revenue by event (Task 6: Only count paid orders)
    const eventRevenueMap = new Map()
    paidOrders.forEach(order => {
      if (order.secret_events) {
        const eventTitle = order.secret_events.title
        if (!eventRevenueMap.has(eventTitle)) {
          eventRevenueMap.set(eventTitle, { revenue: 0, orders: 0 })
        }
        eventRevenueMap.get(eventTitle).revenue += order.total_amount || 0
        eventRevenueMap.get(eventTitle).orders++
      }
    })
    const revenueByEvent = Array.from(eventRevenueMap.entries())
      .map(([eventTitle, stats]) => ({ eventTitle, ...stats }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10)

    // Popular items
    const itemMap = new Map()
    orderItems.forEach(item => {
      const itemName = item.secret_menu_items?.name || 'Unknown Item'
      if (!itemMap.has(itemName)) {
        itemMap.set(itemName, { orders: 0, revenue: 0 })
      }
      itemMap.get(itemName).orders += item.quantity || 0
      itemMap.get(itemName).revenue += item.total_price || 0
    })
    const popularItems = Array.from(itemMap.entries())
      .map(([itemName, stats]) => ({ itemName, ...stats }))
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 10)

    // Order status distribution
    const statusDistribution: Record<string, number> = {}
    orders.forEach(order => {
      statusDistribution[order.status] = (statusDistribution[order.status] || 0) + 1
    })

    // Calculate growth rates (Task 6: Only count paid orders)
    const thisMonthRevenue = paidOrders
      .filter(o => new Date(o.created_at) >= monthAgo)
      .reduce((sum, o) => sum + (o.total_amount || 0), 0)

    const twoMonthsAgo = new Date()
    twoMonthsAgo.setMonth(now.getMonth() - 2)
    const lastMonthRevenue = paidOrders
      .filter(o => {
        const orderDate = new Date(o.created_at)
        return orderDate >= twoMonthsAgo && orderDate < monthAgo
      })
      .reduce((sum, o) => sum + (o.total_amount || 0), 0)

    const revenueGrowthRate = lastMonthRevenue > 0
      ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
      : 0

    // Customer growth rate
    const lastMonthCustomers = users.filter(u => {
      const userDate = new Date(u.created_at)
      return userDate >= twoMonthsAgo && userDate < monthAgo
    }).length

    const customerGrowthRate = lastMonthCustomers > 0
      ? ((newCustomersThisMonth - lastMonthCustomers) / lastMonthCustomers) * 100
      : 0

    // Calculate average time from event creation to first order
    const eventTimings = events
      .map(event => {
        const eventCreated = new Date(event.created_at)
        const eventOrders = orders.filter(o => o.secret_events?.id === event.id)

        if (eventOrders.length === 0) return null

        const firstOrder = eventOrders.reduce((earliest, order) => {
          const orderDate = new Date(order.created_at)
          return !earliest || orderDate < new Date(earliest.created_at) ? order : earliest
        }, null as any)

        if (!firstOrder) return null

        const firstOrderDate = new Date(firstOrder.created_at)
        const minutesDiff = (firstOrderDate.getTime() - eventCreated.getTime()) / (1000 * 60)
        return minutesDiff > 0 ? minutesDiff : null
      })
      .filter(time => time !== null && time > 0) as number[]

    const averageTimeToOrder = eventTimings.length > 0
      ? eventTimings.reduce((sum, time) => sum + time, 0) / eventTimings.length
      : 0

    // Phase 6: Capacity Utilization Metrics
    const eventsWithCapacity = events.filter(e => e.max_lobster_tails && e.max_lobster_tails > 0)

    // Calculate total tails ordered and capacity across all events
    const totalTailCapacity = eventsWithCapacity.reduce((sum, e) => sum + (e.max_lobster_tails || 0), 0)

    // For total tails ordered, we need to sum up order items with lobster tails
    // We'll use the RPC function results or estimate from menu items
    const totalTailsOrdered = orderItems.reduce((sum, item) => {
      const menuItem = menuItems.find(m => m.name === item.secret_menu_items?.name)
      const tailQty = menuItem?.lobster_tail_qty || 0
      return sum + (tailQty * (item.quantity || 0))
    }, 0)

    const averageUtilization = totalTailCapacity > 0
      ? Math.round((totalTailsOrdered / totalTailCapacity) * 100)
      : 0

    // Count sold-out events (100% capacity)
    const soldOutEvents = eventsWithCapacity.filter(event => {
      const eventOrders = orderItems.filter(item => {
        const order = orders.find(o => o.id === item.secret_orders?.id)
        return order?.secret_events?.id === event.id
      })

      const eventTailsOrdered = eventOrders.reduce((sum, item) => {
        const menuItem = menuItems.find(m => m.name === item.secret_menu_items?.name)
        const tailQty = menuItem?.lobster_tail_qty || 0
        return sum + (tailQty * (item.quantity || 0))
      }, 0)

      return event.max_lobster_tails && eventTailsOrdered >= event.max_lobster_tails
    }).length

    // Group events by capacity tier
    const eventsByTier = [
      { tier: '0-25%', count: 0 },
      { tier: '25-50%', count: 0 },
      { tier: '50-75%', count: 0 },
      { tier: '75-100%', count: 0 },
      { tier: '100%', count: 0 }
    ]

    eventsWithCapacity.forEach(event => {
      const eventOrders = orderItems.filter(item => {
        const order = orders.find(o => o.id === item.secret_orders?.id)
        return order?.secret_events?.id === event.id
      })

      const eventTailsOrdered = eventOrders.reduce((sum, item) => {
        const menuItem = menuItems.find(m => m.name === item.secret_menu_items?.name)
        const tailQty = menuItem?.lobster_tail_qty || 0
        return sum + (tailQty * (item.quantity || 0))
      }, 0)

      const percent = event.max_lobster_tails
        ? (eventTailsOrdered / event.max_lobster_tails) * 100
        : 0

      if (percent >= 100) eventsByTier[4].count++
      else if (percent >= 75) eventsByTier[3].count++
      else if (percent >= 50) eventsByTier[2].count++
      else if (percent >= 25) eventsByTier[1].count++
      else eventsByTier[0].count++
    })

    // Phase 6: Package Popularity by Tail Count
    const tailCountMap = new Map<number, { itemCount: number; totalOrders: number; revenue: number }>()

    menuItems.forEach(menuItem => {
      const tailQty = menuItem.lobster_tail_qty || 0

      // Find all order items for this menu item
      const itemOrders = orderItems.filter(oi => oi.secret_menu_items?.name === menuItem.name)
      const totalQuantity = itemOrders.reduce((sum, oi) => sum + (oi.quantity || 0), 0)
      const totalRev = itemOrders.reduce((sum, oi) => sum + (oi.total_price || 0), 0)

      if (!tailCountMap.has(tailQty)) {
        tailCountMap.set(tailQty, { itemCount: 0, totalOrders: 0, revenue: 0 })
      }

      const existing = tailCountMap.get(tailQty)!
      existing.itemCount++
      existing.totalOrders += totalQuantity
      existing.revenue += totalRev
    })

    const packagePopularity = Array.from(tailCountMap.entries())
      .map(([tailCount, stats]) => ({
        tailCount,
        itemCount: stats.itemCount,
        totalOrders: stats.totalOrders,
        revenue: stats.revenue
      }))
      .sort((a, b) => b.totalOrders - a.totalOrders)

    return {
      totalCustomers,
      newCustomersThisMonth,
      returningCustomers,
      customerGrowthRate,

      totalOrders: orders.length,
      averageOrderValue,
      orderCompletionRate,
      orderTrends,

      totalEvents,
      averageEventCapacity,
      eventUtilizationRate,
      popularLocations,

      totalRevenue,
      revenueThisMonth: thisMonthRevenue,
      revenueGrowthRate,
      revenueByEvent,

      averageTimeToOrder: Math.round(averageTimeToOrder),
      popularItems,
      orderStatusDistribution: statusDistribution,
      customerRetentionRate: totalCustomers > 0 ? (returningCustomers / totalCustomers * 100) : 0,

      // Phase 6: Lobster tail capacity analytics
      capacityUtilization: {
        averageUtilization,
        soldOutEvents,
        eventsByTier,
        totalTailsOrdered,
        totalTailCapacity
      },
      packagePopularity
    }
  }

  const generateOrderTrends = (orders: any[], startDate: Date) => {
    const trends = []
    const dayMs = 24 * 60 * 60 * 1000
    
    for (let d = new Date(startDate); d <= new Date(); d.setTime(d.getTime() + dayMs)) {
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      const dayEnd = new Date(dayStart.getTime() + dayMs)
      
      const dayOrders = orders.filter(o => {
        const orderDate = new Date(o.created_at)
        return orderDate >= dayStart && orderDate < dayEnd
      })
      
      trends.push({
        date: dayStart.toISOString().split('T')[0],
        orders: dayOrders.length,
        // Task 6: Only count paid orders for revenue trends
        revenue: dayOrders
          .filter(o => o.status === 'confirmed' || o.status === 'completed')
          .reduce((sum, o) => sum + (o.total_amount || 0), 0)
      })
    }
    
    return trends
  }

  const exportAnalytics = () => {
    if (!data) return

    const csvContent = [
      ['Metric', 'Value'],
      ['Total Customers', data.totalCustomers],
      ['New Customers (30d)', data.newCustomersThisMonth],
      ['Total Orders', data.totalOrders],
      ['Average Order Value', `$${data.averageOrderValue.toFixed(2)}`],
      ['Total Revenue', `$${data.totalRevenue.toFixed(2)}`],
      ['Order Completion Rate', `${data.orderCompletionRate.toFixed(1)}%`],
      ['Customer Retention Rate', `${data.customerRetentionRate.toFixed(1)}%`],
      ['Event Utilization Rate', `${data.eventUtilizationRate.toFixed(1)}%`]
    ].map(row => row.join(',')).join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)

    toast.custom((t) => (
      <EnterpriseToast
        t={t}
        type='success'
        title='Export Complete'
        message='Analytics data exported successfully'
      />
    ))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <BarChart3 size={64} className="text-secret-text-tertiary mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">No Analytics Data</h3>
        <p className="text-secret-text-secondary">Unable to load analytics data.</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white">Analytics Dashboard</h2>
          <p className="text-secret-text-secondary">
            Customer insights and business metrics
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Time Range Filter */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="secret-select"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          
          <button
            onClick={exportAnalytics}
            className="secret-btn-secondary flex items-center space-x-2"
          >
            <Download size={16} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-secret-bg-secondary rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Users className="text-blue-500" size={32} />
            <div className="text-right">
              <div className="text-2xl font-bold text-white">{data.totalCustomers}</div>
              <div className="text-secret-text-secondary text-sm">Total Customers</div>
            </div>
          </div>
          <div className="flex items-center text-green-500 text-sm">
            <TrendingUp size={14} className="mr-1" />
            <span>+{data.newCustomersThisMonth} this month</span>
          </div>
        </div>

        <div className="bg-secret-bg-secondary rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Package className="text-orange-500" size={32} />
            <div className="text-right">
              <div className="text-2xl font-bold text-white">{data.totalOrders}</div>
              <div className="text-secret-text-secondary text-sm">Total Orders</div>
            </div>
          </div>
          <div className="flex items-center text-green-500 text-sm">
            <Target size={14} className="mr-1" />
            <span>{data.orderCompletionRate.toFixed(1)}% completion</span>
          </div>
        </div>

        <div className="bg-secret-bg-secondary rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="text-secret-primary" size={32} />
            <div className="text-right">
              <div className="text-2xl font-bold text-secret-primary">
                {formatPrice(data.totalRevenue * 100)}
              </div>
              <div className="text-secret-text-secondary text-sm">Total Revenue</div>
            </div>
          </div>
          <div className="flex items-center text-green-500 text-sm">
            <TrendingUp size={14} className="mr-1" />
            <span>{formatPrice(data.averageOrderValue * 100)} avg order</span>
          </div>
        </div>

        <div className="bg-secret-bg-secondary rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Percent className="text-purple-500" size={32} />
            <div className="text-right">
              <div className="text-2xl font-bold text-white">
                {data.customerRetentionRate.toFixed(1)}%
              </div>
              <div className="text-secret-text-secondary text-sm">Retention Rate</div>
            </div>
          </div>
          <div className="flex items-center text-purple-500 text-sm">
            <Star size={14} className="mr-1" />
            <span>{data.returningCustomers} returning</span>
          </div>
        </div>
      </div>

      {/* Charts and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Popular Items */}
        <div className="bg-secret-bg-secondary rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6">Popular Items</h3>
          <div className="space-y-4">
            {data.popularItems.slice(0, 5).map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-yellow-500 text-black' :
                    index === 1 ? 'bg-gray-400 text-black' :
                    index === 2 ? 'bg-orange-600 text-white' :
                    'bg-secret-bg-tertiary text-secret-text-secondary'
                  }`}>
                    {index + 1}
                  </div>
                  <span className="text-white font-medium">{item.itemName}</span>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold">{item.orders} orders</div>
                  <div className="text-secret-primary text-sm">
                    {formatPrice(item.revenue * 100)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue by Event */}
        <div className="bg-secret-bg-secondary rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6">Top Events by Revenue</h3>
          <div className="space-y-4">
            {data.revenueByEvent.slice(0, 5).map((event, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-white font-medium truncate">{event.eventTitle}</div>
                  <div className="text-secret-text-secondary text-sm">
                    {event.orders} orders
                  </div>
                </div>
                <div className="text-secret-primary font-bold ml-4">
                  {formatPrice(event.revenue * 100)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Popular Locations */}
        <div className="bg-secret-bg-secondary rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6">Popular Locations</h3>
          <div className="space-y-4">
            {data.popularLocations.map((location, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MapPin size={16} className="text-secret-primary" />
                  <span className="text-white">{location.location}</span>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold">{location.events} events</div>
                  <div className="text-secret-text-secondary text-sm">
                    {location.orders} orders
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Status Distribution */}
        <div className="bg-secret-bg-secondary rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6">Order Status</h3>
          <div className="space-y-3">
            {Object.entries(data.orderStatusDistribution).map(([status, count]) => {
              const percentage = (count / data.totalOrders * 100).toFixed(1)
              return (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-white capitalize">{status}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 text-right text-secret-text-secondary text-sm">
                      {percentage}%
                    </div>
                    <div className="w-20 bg-secret-bg-tertiary rounded-full h-2">
                      <div 
                        className="bg-secret-primary h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-8 text-white text-sm font-medium">
                      {count}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Event Performance */}
      <div className="bg-secret-bg-secondary rounded-lg p-6">
        <h3 className="text-xl font-bold text-white mb-6">Event Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <Calendar className="text-secret-primary mx-auto mb-2" size={32} />
            <div className="text-2xl font-bold text-white">{data.totalEvents}</div>
            <div className="text-secret-text-secondary text-sm">Total Events</div>
          </div>

          <div className="text-center">
            <Target className="text-orange-500 mx-auto mb-2" size={32} />
            <div className="text-2xl font-bold text-white">
              {data.eventUtilizationRate.toFixed(1)}%
            </div>
            <div className="text-secret-text-secondary text-sm">Utilization Rate</div>
          </div>

          <div className="text-center">
            <Users className="text-blue-500 mx-auto mb-2" size={32} />
            <div className="text-2xl font-bold text-white">
              {Math.round(data.averageEventCapacity)}
            </div>
            <div className="text-secret-text-secondary text-sm">Avg Capacity</div>
          </div>
        </div>
      </div>

      {/* Phase 6: Capacity Utilization Report */}
      <div className="bg-secret-bg-secondary rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <span className="text-3xl">🦞</span>
          <h3 className="text-xl font-bold text-white">Lobster Tail Capacity Utilization</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="text-center p-4 bg-secret-bg-tertiary rounded-lg">
            <div className="text-2xl font-bold text-secret-primary">
              {data.capacityUtilization.averageUtilization}%
            </div>
            <div className="text-secret-text-secondary text-sm mt-1">Average Utilization</div>
          </div>

          <div className="text-center p-4 bg-secret-bg-tertiary rounded-lg">
            <div className="text-2xl font-bold text-white">
              {data.capacityUtilization.totalTailsOrdered}
            </div>
            <div className="text-secret-text-secondary text-sm mt-1">Tails Ordered</div>
          </div>

          <div className="text-center p-4 bg-secret-bg-tertiary rounded-lg">
            <div className="text-2xl font-bold text-white">
              {data.capacityUtilization.totalTailCapacity}
            </div>
            <div className="text-secret-text-secondary text-sm mt-1">Total Capacity</div>
          </div>

          <div className="text-center p-4 bg-secret-bg-tertiary rounded-lg">
            <div className="text-2xl font-bold text-red-500">
              {data.capacityUtilization.soldOutEvents}
            </div>
            <div className="text-secret-text-secondary text-sm mt-1">Sold Out Events</div>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-semibold text-white mb-4">Events by Capacity Tier</h4>
          <div className="space-y-3">
            {data.capacityUtilization.eventsByTier.map((tier, index) => {
              const maxCount = Math.max(...data.capacityUtilization.eventsByTier.map(t => t.count))
              const percentage = maxCount > 0 ? (tier.count / maxCount) * 100 : 0

              // Color coding based on tier
              const getColor = () => {
                if (tier.tier === '100%') return 'bg-red-500'
                if (tier.tier === '75-100%') return 'bg-orange-500'
                if (tier.tier === '50-75%') return 'bg-yellow-500'
                if (tier.tier === '25-50%') return 'bg-blue-500'
                return 'bg-green-500'
              }

              return (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-24 text-secret-text-secondary text-sm">{tier.tier}</div>
                  <div className="flex-1">
                    <div className="bg-secret-bg-tertiary rounded-full h-2">
                      <div
                        className={`${getColor()} h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-12 text-white text-sm font-medium text-right">
                    {tier.count}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Phase 6: Package Popularity Report */}
      <div className="bg-secret-bg-secondary rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Package className="text-secret-primary" size={32} />
          <h3 className="text-xl font-bold text-white">Package Popularity by Tail Count</h3>
        </div>

        {data.packagePopularity.length > 0 ? (
          <div className="space-y-4">
            {data.packagePopularity.map((pkg, index) => {
              const maxRevenue = Math.max(...data.packagePopularity.map(p => p.revenue))
              const revenuePercentage = maxRevenue > 0 ? (pkg.revenue / maxRevenue) * 100 : 0

              return (
                <div key={index} className="border border-secret-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">🦞</div>
                      <div>
                        <div className="text-lg font-bold text-white">
                          {pkg.tailCount === 0 ? 'No Tails' : `${pkg.tailCount} Tail${pkg.tailCount !== 1 ? 's' : ''}`}
                        </div>
                        <div className="text-secret-text-tertiary text-sm">
                          {pkg.itemCount} menu item{pkg.itemCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-secret-primary">
                        {formatPrice(pkg.revenue * 100)}
                      </div>
                      <div className="text-secret-text-secondary text-sm">
                        {pkg.totalOrders} order{pkg.totalOrders !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  <div className="bg-secret-bg-tertiary rounded-full h-2">
                    <div
                      className="bg-secret-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${revenuePercentage}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-secret-text-secondary">
            No package data available
          </div>
        )}
      </div>
    </div>
  )
}

export default Analytics